import { useCallback, useEffect, useState } from "react";

export const normalizeDateOption = (value) =>
  String(value || "today")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");

export const EMPTY_CHART_ROWS = [];

export function useChartPeriod(pageDateOption, pageStart = "", pageEnd = "") {
  const normalizedPage = normalizeDateOption(pageDateOption);
  const pageKey = `${normalizedPage}|${pageStart || ""}|${pageEnd || ""}`;
  const [pageKeySeen, setPageKeySeen] = useState(pageKey);
  const [dateOption, setDateOption] = useState(normalizedPage);
  const [customStart, setCustomStart] = useState(pageStart || "");
  const [customEnd, setCustomEnd] = useState(pageEnd || "");

  if (pageKeySeen !== pageKey) {
    setPageKeySeen(pageKey);
    setDateOption(normalizedPage);
    setCustomStart(pageStart || "");
    setCustomEnd(pageEnd || "");
  }

  const setPeriod = useCallback((value) => {
    const next = normalizeDateOption(value);
    setDateOption(next);
    if (next !== "custom") {
      setCustomStart("");
      setCustomEnd("");
    }
  }, []);

  const setCustomRange = useCallback((start, end) => {
    setDateOption("custom");
    setCustomStart(start || "");
    setCustomEnd(end || "");
  }, []);

  const matchesPage =
    dateOption === normalizedPage &&
    (dateOption !== "custom" ||
      ((customStart || "") === (pageStart || "") &&
        (customEnd || "") === (pageEnd || "")));
  const isReady = dateOption !== "custom" || Boolean(customStart && customEnd);

  return {
    dateOption,
    customStart,
    customEnd,
    setPeriod,
    setCustomRange,
    matchesPage,
    isReady,
  };
}

export function useChartValue(period, sharedValue, loadValue, emptyValue = null) {
  const [value, setValue] = useState(emptyValue);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (period.matchesPage || !period.isReady) {
      setLoading(false);
      setError("");
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.resolve()
      .then(loadValue)
      .then((next) => {
        if (!cancelled) setValue(next ?? emptyValue);
      })
      .catch((err) => {
        if (cancelled) return;
        setValue(emptyValue);
        setError(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            "",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    emptyValue,
    loadValue,
    period.customEnd,
    period.customStart,
    period.dateOption,
    period.isReady,
    period.matchesPage,
  ]);

  if (!period.isReady && !period.matchesPage) {
    return { value: emptyValue, loading: false, error: "" };
  }

  return {
    value: period.matchesPage ? sharedValue : value,
    loading: period.matchesPage ? false : loading,
    error: period.matchesPage ? "" : error,
  };
}
